import React, { useEffect, useState } from 'react';
import { ChannelGroup } from '../../../shared/types';
import { Box, FormControl, MenuItem, Select } from '@mui/material';

const truncateLabel = (label: string, maxLength = 28): string =>
    label.length > maxLength ? `${label.slice(0, maxLength - 1)}…` : label;

interface TableRowSelectProps {
    value: string;
    groups: ChannelGroup[];
    className?: string;
    onChange: (value: string) => void;
}

const TableRowSelect: React.FC<TableRowSelectProps> = ({
    value,
    groups,
    className,
    onChange,
}) => {
    const [selectedValue, setSelectedValue] = useState<string>(value || '');

    useEffect(() => {
        setSelectedValue(value || '');
    }, [value]);

    const selectedGroupName =
        groups.find((g) => g._id === selectedValue)?.name || 'No group';

    return (
        <FormControl
            size="small"
            sx={{
                width: '100%',
                maxWidth: 200,
                '& .MuiInputBase-root': {
                    height: 30,
                    alignItems: 'center',
                },
                '& .MuiOutlinedInput-notchedOutline': {
                    border: 'none',
                },
                '& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline':
                    {
                        border: 'none',
                    },
                '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline':
                    {
                        border: 'none',
                    },
                '& .MuiInput-underline:before, & .MuiInput-underline:after, & .MuiInput-underline:hover:not(.Mui-disabled):before':
                    {
                        borderBottom: 'none !important',
                    },
                '& .MuiFilledInput-underline:before, & .MuiFilledInput-underline:after, & .MuiFilledInput-underline:hover:not(.Mui-disabled):before':
                    {
                        borderBottom: 'none !important',
                    },
                '&& .MuiSelect-select': {
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'flex-start',
                    textAlign: 'left',
                    fontSize: '0.75rem',
                    fontWeight: 400,
                    lineHeight: 1.2,
                    minHeight: 'unset',
                    minWidth: 0,
                    overflow: 'hidden',
                    paddingLeft: '0',
                    paddingTop: '0',
                    paddingBottom: '0',
                    paddingRight: '30px',
                },
                '&& .MuiOutlinedInput-input': {
                    paddingLeft: '0',
                },
            }}
        >
            <Select
                className={className}
                variant="outlined"
                value={selectedValue}
                MenuProps={{
                    slotProps: {
                        paper: {
                            sx: {
                                '& .MuiMenuItem-root': {
                                    fontSize: '0.75rem',
                                    minHeight: 30,
                                    paddingTop: 0.5,
                                    paddingBottom: 0.5,
                                    paddingLeft: 1.25,
                                    paddingRight: 1.25,
                                },
                            },
                        },
                    },
                }}
                renderValue={(selected) => {
                    const selectedLabel =
                        groups.find((g) => g._id === selected)?.name ||
                        'No group';

                    return (
                        <Box
                            component="span"
                            sx={{
                                display: 'block',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                                width: '100%',
                                textAlign: 'left',
                            }}
                            title={selectedLabel}
                        >
                            {selectedLabel}
                        </Box>
                    );
                }}
                onChange={(e) => {
                    const nextValue = e.target.value;
                    setSelectedValue(nextValue);
                    onChange(nextValue);
                }}
                title={selectedGroupName}
            >
                <MenuItem value="">No group</MenuItem>
                {groups.map((g) => (
                    <MenuItem key={g._id} value={g._id} title={g.name}>
                        {truncateLabel(g.name)}
                    </MenuItem>
                ))}
            </Select>
        </FormControl>
    );
};

export default TableRowSelect;
